import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Link } from 'react-router-dom';
import { message, notification } from 'antd';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import { User, Lock, KeyRound } from 'lucide-react';

import UPDATE_USER from '../../../.././core/GraphQl/Mutations/UPDATE_USER';
import AUTHENTICATED_USER from '../../../../core/GraphQl/Mutations/AUTHENTICATED_USER';

import {
  getTopRole,
  getRolesFeatures,
} from '../../../../core/utils/dataManipulating/organization';

import { Button } from '@/components/ui/button';

import AuthShell from './AuthShell';
import Field from './Field';
import ToggleVisibility from './ToggleVisibility';
import { usePermify } from '@permify/react-role';

const USERNAME_PATTERN = /^[A-Za-z0-9]+$/;

const SignIn = ({ authenticatedUser, updateUser, history }) => {
  const { setUser: setPermifyUser } = usePermify();
  const { search } = window.location;

  document.title = window.location.hostname;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaUser, setMfaUser] = useState(null);

  useEffect(() => {
    localStorage.removeItem('userData');
  }, []);

  const validate = () => {
    const next = {};
    if (!username) next.username = 'Username is required';
    else if (!USERNAME_PATTERN.test(username))
      next.username = 'Username must be all one word and not contain spaces';
    if (!password) next.password = 'Please input your password';
    if (mfaUser && !code) next.code = 'Enter the MFA code';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const loginUser = async (signedInUser) => {
    try {
      const response = await authenticatedUser({ variables: {} });
      const {
        authenticatedUser: {
          id,
          username,
          config,
          email,
          cognitoUserId,
          avatar,
          roles,
          notifications,
          ssoToken,
          campaignTableDisplayColumns,
          timezone,
        },
      } = response.data;

      const features = getRolesFeatures(roles);
      // Strip the internal MOJO enrollment marker — it gates no UI, just indicates org enrollment
      const enabledFeatures = features.filter((feature) => feature !== 'ENABLED');

      const role = getTopRole(roles);
      const isAdmin = config ? config.isAdmin : false;

      const isPreviewOrg = false;

      const userData = {
        isAuthenticated: true,
        id,
        username,
        email,
        cognitoUserId,
        avatar,
        features: enabledFeatures,
        notifications,
        defaultRole: role,
        role,
        roles,
        isAdmin,
        isPreviewOrg,
        permission: config,
        ssoToken,
        campaignTableDisplayColumns:
          campaignTableDisplayColumns && campaignTableDisplayColumns.length
            ? campaignTableDisplayColumns
            : [],
        config,
        timezone: timezone,
      };

      const level = userData && userData.role && userData.role.org && userData.role.org.level;
      let userRoles = [];

      if (config) {
        userRoles = Object.keys(userData.config)
          .filter((key) => key.startsWith('is'))
          .filter((key) => userData.config[key]);
      }
      if (level) userRoles.push(level);

      setPermifyUser({
        id,
        roles: userRoles,
        permissions: ['default'],
      });

      await updateUser({
        variables: { ...userData },
      });

      //If Some of the features on home page is added. Turn this on to redirect home page when logging in
      //history.push("/home");
      const searchParams = new URLSearchParams(search);
      const redirectUrl = searchParams.get('redirectUrl');
      if (redirectUrl) {
        history.push(redirectUrl);
      }

      notification.close('sessionExpired');
      message.success("Success! You're now signed in!");
    } catch (err) {
      console.log(err);
      message.error('Failure signing in', err);
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mfaUser) return submitMFA();
    if (!validate()) return;

    setLoading(true);
    Auth.signOut().then(() => {
      if (!Auth || typeof Auth.signIn !== 'function') {
        setLoading(false);
        throw new Error(
          'No Auth module found, please ensure @aws-amplify/auth is imported'
        );
      }

      Auth.signIn(username, password)
        .then((u) => {
          if (
            u.challengeName === 'SMS_MFA' ||
            u.challengeName === 'SOFTWARE_TOKEN_MFA'
          ) {
            setMfaUser(u);
            setLoading(false);
          } else if (u.challengeName === 'NEW_PASSWORD_REQUIRED') {
            setLoading(false);
            history.push({
              pathname: '/new',
              state: { username, password },
            });
            message.warning(
              'You are currently using a temporary password, you are required to change your password'
            );
          } else if (u.challengeName === 'MFA_SETUP') {
            setLoading(false);
            history.push('/setup');
          } else {
            loginUser(u);
          }
        })
        .catch((err) => {
          if (err.code === 'UserNotConfirmedException') {
            message.warning('Sign up confirmation not completed!');
          } else if (err.code === 'PasswordResetRequiredException') {
            history.push('/recover');
            message.warning(err.message);
          } else {
            message.error(err.message);
          }
          Auth.signOut();
          setLoading(false);
        });
    });
  };

  const submitMFA = () => {
    if (!validate()) return;
    setLoading(true);
    if (
      mfaUser.challengeName === 'SMS_MFA' ||
      mfaUser.challengeName === 'SOFTWARE_TOKEN_MFA'
    ) {
      Auth.confirmSignIn(mfaUser, code, mfaUser.challengeName)
        .then((data) => loginUser(data))
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } else {
      Auth.signOut().then(() => {
        setLoading(false);
        message.warning(
          'Something went wrong. Please sign in again or contact an administrator.'
        );
      });
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Manage campaigns, track attribution, and report with confidence."
      onLogoClick={() => {
        setUsername('');
        setPassword('');
        setCode('');
        setMfaUser(null);
        setErrors({});
        history.push('/');
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          <Field
            id="signin-username"
            label="Username"
            icon={User}
            type="text"
            autoComplete="username"
            placeholder="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={errors.username}
          />

          <Field
            id="signin-password"
            label="Password"
            icon={Lock}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            rightAdornment={
              <ToggleVisibility
                shown={showPassword}
                onClick={() => setShowPassword((s) => !s)}
              />
            }
          />

          {mfaUser ? (
            <Field
              id="signin-code"
              label="Verification code"
              icon={KeyRound}
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={errors.code}
            />
          ) : null}
        </div>

        {!mfaUser && (
          <div className="mb-6 mt-4 flex items-center justify-end">
            <Link
              to="/recover"
              className="text-[13px] font-medium text-[#1890ff] hover:text-[#096dd9]"
            >
              Forgot password?
            </Link>
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#1890ff] hover:bg-[#096dd9]"
        >
          {loading
            ? mfaUser
              ? 'Verifying…'
              : 'Signing in…'
            : mfaUser
            ? 'Confirm Code'
            : 'Sign In'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default compose(
  graphql(UPDATE_USER, { name: 'updateUser' }),
  graphql(AUTHENTICATED_USER, { name: 'authenticatedUser' })
)(SignIn);
